<template>
  <basic-container>
    <div class="app-screen-config">
      <div class="app-screen-config-left">
        <el-form :model="form" :rules="rules" ref="form" label-width="100px">
          <el-form-item label="配置名称" prop="name">
            <el-input v-model="form.name" placeholder="配置名称" style="width: 600px;"/>
          </el-form-item>
          <!-- 背景图 -->
          <el-form-item label="背景图" prop="backgroundImage">
            <el-upload 
              class="avatar-uploader" 
              :before-upload="fileCheck" 
              :http-request="upload" 
              :show-file-list="false"
            >
              <el-image 
                v-if="form.backgroundImage" 
                :src="form.backgroundImage" 
                fit="scale-down" 
                class="avatar" 
              />
              <i v-else class="el-icon-plus avatar-uploader-icon"></i>
            </el-upload>
          </el-form-item>
          <!-- 备注 -->
          <el-form-item label="备注" prop="remark">
            <el-input v-model="form.remark" placeholder="备注" style="width: 600px;" type="textarea" :rows="3"/>
          </el-form-item>
          <!-- 行配置 -->
          <el-form-item label="行配置" prop="rowData">
            <el-button type="primary" @click="addRow">添加行</el-button>
          </el-form-item>
          <el-table 
            :row-class-name="tableRowClassName"
            @cell-mouse-enter="handleCellMouseEnter"
            @cell-mouse-leave="handleCellMouseLeave"
            :data="tableData"
            border 
            max-height="440" 
            style="width: 770px;"
            :span-method="objectSpanMethod"
          >
            <!-- 行号 -->
            <el-table-column prop="id" label="#" width="50" align="center" header-align="center" />
            <!-- 行高 -->
            <el-table-column prop="componentHeight" label="行高" width="80" align="center" header-align="center" >
              <template #default="{ row }">
                {{ row.componentHeight }}px
              </template>
            </el-table-column>
            <!-- 组件图示 -->
            <el-table-column prop="componentImage" label="组件图示" width="250" align="center">
              <template #default="{ row }">
                <div class="image-cell">
                  <el-image 
                    v-if="row.componentImage" 
                    style="width: 100%; height: 50px; display: block; cursor: pointer;"
                    :src="row.componentImage" 
                    fit="scale-down"
                    :preview-src-list="[row.componentImage]"
                  />
                  <span v-else>暂无图片</span>
                </div>
              </template>
            </el-table-column>
            <!-- 响应类型 -->
            <el-table-column prop="responseType" label="响应类型" width="100" align="center" header-align="center">
              <template #default="{ row }">
                {{ getComponentTypeLabel(row.responseType) }}
              </template>
            </el-table-column>
            <!-- 名称 -->
            <el-table-column prop="componentName" label="名称" width="100" align="center" header-align="center" show-overflow-tooltip />
            <!-- 扩展字段 -->
            <el-table-column prop="componentExt" label="扩展字段" width="120" align="center" header-align="center" show-overflow-tooltip />
            <!-- 组件宽度 -->
            <el-table-column prop="componentWidth" label="组件宽度" width="80" align="center" header-align="center">
              <template #default="{ row }">
                {{ row.componentWidth }}px
              </template>
            </el-table-column>
            <!-- 排序 -->
            <el-table-column prop="sort" label="排序" width="100" align="center">
              <template #default="{ row }">
                <el-button type="text" @click="handleUp(row)">上移</el-button>
                <el-button type="text" @click="handleDown(row)">下移</el-button>
              </template>
            </el-table-column>
            <!-- 操作 -->
            <el-table-column prop="handle" label="操作" align="center">
              <template #default="{ row }">
                <el-button type="text" @click="handleEdit(row)">编辑</el-button>
                <el-button type="text" @click="handleRemove(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <!-- 状态 -->
          <el-form-item label="状态" prop="status" style="margin-top: 20px;">
            <el-radio-group v-model="form.status">
              <el-radio :label="1">生效</el-radio>
              <el-radio :label="0">失效</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </div>
      <div class="app-screen-config-right">
        <div class="demonstration-area-container">
          <template v-if="tableData.length > 0">
            <div class="demonstration-area-row" v-for="item in form.rowData" :key="item.id" :style="{ height: item.componentHeight + 'px' }">
              <div class="demonstration-area-row-item" v-for="rowItem in item.rowComponent" :key="rowItem.id" :style="{ width: rowItem.componentWidth + 'px', height: item.componentHeight + 'px' }">
                <el-image :src="rowItem.componentImage" fit="fill" style="width: 100%; height: 100%;" />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="demonstration-area-row-no-data">暂无数据</div>
          </template>
        </div>
        <div class="bottom-button">
          <el-button @click="$emit('back')">返回</el-button>
          <el-button type="primary" @click="submitForm">保存</el-button>
        </div>
      </div>
    </div>
    <EditRowDialog ref="editRowDialog" @saveRowComponent="saveRowComponent" @addRowComponent="addRowComponent" :componentTypeList="componentTypeList" />
  </basic-container>
</template>

<script>
import Message from "@/components/Message";
import axios from "axios";
import EditRowDialog from "./components/editRowDialog.vue";
import { getComponentTypeList, addConfig, updateConfig, getDetail } from "@/api/apps/appScreenManage";

/**
 * 行配置序列化后的落库字节上限。
 * 整份配置的所有行、所有组件会被后端整体 JSON 序列化，存入 app_screen_cast_config.row_config 单个 TEXT 列，
 * TEXT 硬上限 65535 字节；此处取 60000，留 5535 字节余量应对 JSON 结构开销波动。
 * 溢出后果：MySQL 严格模式直接报错；非严格模式静默截断导致整份 JSON 损坏、客户端解析失败。
 */
const ROW_CONFIG_MAX_BYTES = 60000;

/**
 * 行配置体积预警线（字节）。达到该值说明已接近 ROW_CONFIG_MAX_BYTES 硬上限，
 * 仅提醒运营留意剩余余量，不阻止本次保存。
 */
const ROW_CONFIG_WARN_BYTES = 40000;

export default {
  name: "AppScreenConfig",
  components: {
    EditRowDialog
  },
  props: {
    type: {
      type: String,
      default: 'add'
    },
    id: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      form: {
        name: "",
        backgroundImage: "",
        rowData: [],
        remark: "",
        status: 1
      },
      tableData: [],
      currentRowId: '',
      rules: {
        name: [
          { required: true, message: "请输入配置名称", trigger: "blur" }
        ],
        rowData: [
          { required: true, message: "请添加行数据配置", trigger: "blur" }
        ],
        status: [
          { required: true, message: "请选择状态", trigger: "change" }
        ]
      },
      componentTypeList: [],
    };
  },
  created() {
    this.initForm();
  },
  methods: {
    async initForm() {
      if ((this.type === 'edit' || this.type === 'copy') && this.id) {
        try {
          const { data } = await getDetail(this.id);
          if (data.code === 200) {
            this.form = {
              name: this.type === 'copy' ? (data.data.name ? `${data.data.name}_副本` : '') : data.data.name,
              backgroundImage: data.data.bgUrl,
              remark: data.data.remark,
              status: data.data.status,
              rowData: JSON.parse(data.data.rowConfig || '[]').map((row, index) => ({
                id: index + 1,
                rowComponent: row.rowInfo.map(rowItem => ({
                  componentImage: rowItem.img,
                  responseType: rowItem.type.toString(),
                  componentName: rowItem.name,
                  componentExt: rowItem.ext,
                  componentHeight: rowItem.height,
                  componentWidth: rowItem.width,
                  isShowUrlInput: rowItem.isInput,
                  buttonIconImage: rowItem.icon,
                  searchPlaceholder: rowItem.tip
                }))
              }))
            }
          }

          this.tableData = this.getTableData();

        } catch (error) {
          console.error(error);
        }
      }

      this.getComponentTypeList();
    },
    /** 上传图片校验 */
    fileCheck(file) {
      // 判断类型
      if (file.type !== "image/jpeg" && file.type !== "image/png") {
        Message.error("目前只支持jpg/png图片");
        return false;
      }
      // 判断大小
      if (file.size > 1024 * 1024 * 2) {
        Message.error("不能上传超过2M的图片");
        return false;
      }
    },
    /** 上传图片的方法 */
    async upload(params) {
      const file = params.file;
      let formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await axios.post(
          "/lebo-resource/oss/endpoint/put-file?code=lebotob",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (data.code === 200) {
          /** 上传成功 */
          this.form.backgroundImage = data.data.link;
          Message.success("上传成功");
        }
      } catch (error) {
        console.error(error);
        Message.error("上传失败");
      }
    },
    /** 添加行 */
    addRow() {
      if (this.form.rowData.length >= 20) {
        Message.error("最多添加20行");
        return;
      }

      this.$refs.editRowDialog.open('add');
    },
    /** 编辑 */
    handleEdit(row) {
      const currentFormRowData = this.form.rowData.find(item => item.id === row.id);
      
      this.$refs.editRowDialog.open('edit', currentFormRowData, row.id);
    },
    /** 删除行 */
    removeRow(index) {
      this.form.rowData.splice(index, 1);
    },
    /** 提交表单 */
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          try {
            // 处理行数据
            const requestRowData = this.form.rowData.map(rowItem => {
              return {
                rowInfo: rowItem.rowComponent.map(item => {
                  return {
                    img: item.componentImage,
                    type: item.responseType,
                    name: item.componentName,
                    ext: item.componentExt,
                    height: item.componentHeight,
                    width: item.componentWidth,
                    isInput: item.isShowUrlInput,
                    icon: item.buttonIconImage,
                    tip: item.searchPlaceholder
                  }
                })
              }
            })

            // 落库前校验行配置体积，避免超出 TEXT 列上限被截断
            if (!this.checkRowConfigSize(requestRowData)) {
              return;
            }

            // 处理请求数据
            const requestData = {
              name: this.form.name,
              bgUrl: this.form.backgroundImage,
              rowConfig: requestRowData,
              remark: this.form.remark,
              status: this.form.status
            }

            if(this.type === 'add' || this.type === 'copy'){
              const { data } = await addConfig(requestData);
              if(data.code === 200){
                Message.success("保存成功");
                this.$emit('success');
              }
            } else if(this.type === 'edit'){
              requestData.id = this.id;
              const { data } = await updateConfig(requestData);
              if(data.code === 200){
                Message.success("保存成功");
                this.$emit('success');
              }
            }
          } catch (error) {
            console.error(error);
          }
        }
      });
    },
    /** 计算字符串的 UTF-8 字节数（不能用 length，那是字符数，中文会少算 2/3） */
    getByteLength(str) {
      return new TextEncoder().encode(str).length;
    },
    /**
     * 校验行配置序列化后的体积，分两档：
     * 超出硬上限则报错并返回 false 阻止保存；仅进入预警区间时提醒运营但返回 true 放行。
     */
    checkRowConfigSize(rowConfig) {
      const byteLength = this.getByteLength(JSON.stringify(rowConfig));

      if (byteLength > ROW_CONFIG_MAX_BYTES) {
        Message.error(
          `行配置内容过长，无法保存：当前 ${byteLength} 字节，上限 ${ROW_CONFIG_MAX_BYTES} 字节，超出 ${byteLength - ROW_CONFIG_MAX_BYTES} 字节。` +
          `请精简各组件的「扩展字段」内容后重试（1 个汉字约占 3 字节，1 个英文字符约占 1 字节）。`
        );
        return false;
      }

      if (byteLength > ROW_CONFIG_WARN_BYTES) {
        // 此处刻意不走 Message 封装：其内部为单例，弹新消息会关掉上一条，
        // 预警会被保存成功后的 Message.success 顶掉而来不及看清。原生 $message 可与之并存。
        this.$message({
          type: 'warning',
          message: `行配置内容已接近上限：当前 ${byteLength} 字节，上限 ${ROW_CONFIG_MAX_BYTES} 字节，剩余可用 ${ROW_CONFIG_MAX_BYTES - byteLength} 字节。` +
            `不影响本次保存，但继续新增组件或加长「扩展字段」可能超限（1 个汉字约占 3 字节，1 个英文字符约占 1 字节）。`,
          duration: 6000,
          showClose: true
        });
      }

      return true;
    },
    /** 上移 */
    handleUp(row) {
      if(row.rowIndex === 0){
        Message.error("已经是第一行了");
        return;
      }

      const currentRowIndex = this.form.rowData.findIndex(item => item.id === row.id);
      const prevRowIndex = currentRowIndex - 1;

      const tempRowItem = this.form.rowData[currentRowIndex];

      this.form.rowData[currentRowIndex] = this.form.rowData[prevRowIndex];
      this.form.rowData[prevRowIndex] = tempRowItem;

      this.tableData = this.getTableData();
    },
    /** 下移 */
    handleDown(row) {
      if(row.rowIndex === this.form.rowData.length - 1){
        Message.error("已经是最后一行了");
        return;
      }

      const currentRowIndex = this.form.rowData.findIndex(item => item.id === row.id);

      const nextRowIndex = currentRowIndex + 1;

      const tempRowItem = this.form.rowData[currentRowIndex];
      
      this.form.rowData[currentRowIndex] = this.form.rowData[nextRowIndex];
      this.form.rowData[nextRowIndex] = tempRowItem;

      this.tableData = this.getTableData();
    },
    /** 删除 */
    handleRemove(row) {
      this.$confirm('确定要删除此行吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const currentRowIndex = this.form.rowData.findIndex(item => item.id === row.id);
        this.form.rowData.splice(currentRowIndex, 1);
        this.form.rowData.forEach((item, index) => {
          item.id = index + 1;
        });
        this.tableData = this.getTableData();

        this.$message({
          type: 'success',
          message: '删除成功!'
        });
      })
    },
    /** 处理表格数据 */
    getTableData() {
      const data = [];
      this.form.rowData.forEach((row, rowIndex) => {
        
        row.rowComponent.forEach((item, index) => {
          data.push({
            ...item,
            id: row.id,
            parent: row,
            rowIndex: rowIndex,
            componentIndex: index
          });
        });
      });
      
      return data;
    },
    /** 表格合并方法 */
    objectSpanMethod({ row, column }) {
      let rowspan = 1;
      let colspan = 1;

      const cpnLength = row.parent.rowComponent.length;
      
      const mergeConditions = ['#', '排序', '操作'];

      if(mergeConditions.includes(column.label)){
        if(row.componentIndex === 0){
          rowspan = cpnLength;
          colspan = 1;
        } else {
          rowspan = 0;
          colspan = 0;
        }
      }

      return {
        rowspan,
        colspan
      };
    },
    tableRowClassName({ row }) {
      if (row.id === this.currentRowId) {
        return 'current-row';
      }
      return '';
    },
    handleCellMouseEnter(row) {
      this.currentRowId = row.id;
    },
    handleCellMouseLeave() {
      this.currentRowId = '';
    },
    saveRowComponent(rowComponent, id) {
      const currentFormRowData = this.form.rowData.find(item => item.id === id);
      currentFormRowData.rowComponent = rowComponent;
      this.tableData = this.getTableData();
    },
    addRowComponent(rowComponent) {
      this.form.rowData.push({
        id: this.form.rowData.length + 1,
        rowComponent: rowComponent,
      });

      this.tableData = this.getTableData();
    },
    async getComponentTypeList() {
        const { data } = await getComponentTypeList();
        this.componentTypeList = data.data.length > 0 ? data.data.map(item => ({
            label: item.dictValue,
            value: item.dictKey
        })) : [];
    },
    getComponentTypeLabel(responseType) {  
      const currentComponentType = this.componentTypeList.find(item => item.value == responseType);

      return currentComponentType ? currentComponentType.label : '';
    },
  }
};
</script>

<style lang="scss" scoped>
.app-screen-config {
  display: flex;
  .app-screen-config-left {
    flex: 1;
    /deep/ .el-form-item {
      .el-form-item__content {
        margin-left: 115px !important;
      }
    }
  }

  .app-screen-config-right {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 33%;
    padding-left: 20px;
    padding-bottom: 30px;
    .demonstration-area-container {
      box-sizing: border-box;
      padding: 20px;
      width: 375px;
      height: 667px;
      border: 2px solid #caccfc;
      overflow-y: auto;
      .demonstration-area-row {
        margin-bottom: 15px;
        display: flex;
        gap: 15px;
        .demonstration-area-row-item {
          background-color: #f5f7fa;
          border-radius: 5px;
          overflow: hidden;
        }
      }
      .demonstration-area-row-no-data {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        font-size: 16px;
        color: #999;
      }
    }
    .bottom-button {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
      margin-right: 30px;
    }
  }
}

.avatar-uploader {
  /deep/.el-upload {
    cursor: pointer;
    position: relative;
    overflow: hidden;
    border: 1px solid #DCDFE6;
    &:hover {
      border-color: #409EFF;
    }
  }
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 220px;
  height: 100px;
  line-height: 100px;
  text-align: center;
}

.avatar {
  width: 220px;
  height: 100px;
  display: block;
}
</style> 

<style>
.el-table .current-row td{
  background-color: #f5f7fa !important;
}
</style>